import Cookies from "js-cookie";
import { BehaviorSubject, filter, distinctUntilChanged, take, tap } from 'rxjs';
import { updateCart } from './cart.service';
const PIEEYE_COOKIE_NAME = 'PieEyeConsent';
const strictlyNecessarySubject$ = new BehaviorSubject(undefined);
const functionalSubject$ = new BehaviorSubject(undefined);
const PIEEYE_CONSENT_MAP = {
    "pieeye-category-necessary": strictlyNecessarySubject$,
    "pieeye-category-functional": functionalSubject$,
    "pieeye-category-performance": undefined,
    "pieeye-category-preferences": undefined,
    "pieeye-category-targeting-analytics": undefined,
    "pieeye-category-unclassified": undefined
};
const DISPLAY_TO_CATEGORY_MAP = {
    "Strictly Necessary": "pieeye-category-necessary",
    "Functionality": "pieeye-category-functional",
    "Performance": "pieeye-category-performance",
    "Preferences": "pieeye-category-preferences",
    "Targeting/Advertising": "pieeye-category-targeting-analytics"
};
// these observables will fire each time the consent is updated
export const strictlyNecessaryCookieConsent$ = buildCookieObservable(strictlyNecessarySubject$);
export const functionalCookieConsent$ = buildCookieObservable(functionalSubject$);
// these observables will fire only once when the cookie in question is allowed
// used when setting up modules to prevent re-initializing scripts
export const strictlyNecessaryCookieConsentAllowedOnce$ = buildAllowedOnce(strictlyNecessaryCookieConsent$).pipe(tap(() => console.log("strictly necessary consent allowed once")));
export const functionalCookieConsentAllowedOnce$ = buildAllowedOnce(functionalCookieConsent$).pipe(tap(() => console.log("functional consent allowed once")));
/**
 * checks various places for pieeye consent data
 * one note is that sometimes only localstorage is populated, while window.PieEyeConsent is a empty object and the cookie is missing
 * pieeye mentioned that this shouldn't happen, but they were unable to reproduce the issue so we're handling it the best we can
 * @returns the pieeye consent object
 */
function getConsent() {
    let consent = {};
    if (!isEmptyObject(window.PieEyeConsent)) {
        consent = window.PieEyeConsent;
    }
    else if (Cookies.get(PIEEYE_COOKIE_NAME)) {
        consent = JSON.parse(decodeURIComponent(Cookies.get(PIEEYE_COOKIE_NAME)));
    }
    else {
        // localstorage contains display names rather than category keys for some reason
        const storedConsent = window.localStorage.getItem('pieeye-consent') || window.localStorage.getItem('pieeye-categories-consent');
        if (storedConsent) {
            consent = storedConsent.split(",").map(s => s.trim()).reduce((acc, curr) => {
                const categoryKey = DISPLAY_TO_CATEGORY_MAP[curr];
                if (categoryKey) {
                    acc[categoryKey] = 1;
                }
                return acc;
            }, {});
        }
    }
    return consent;
}
export function allowsFunctionalSync() {
    var _a;
    const functionalConsent = ((_a = getConsent()) === null || _a === void 0 ? void 0 : _a["pieeye-category-functional"]) === 1;
    console.log('consents to functional', functionalConsent);
    return functionalConsent;
}
onConsentUpdated(); // init
// listening for cookie changes because the "pieEyeShopifyConsent" event seems like it only happens
// when the customer changes consent themselves; it does not capture when the consent is set programmatically
if (window.cookieStore) { // doesn't exist on older versions of safari and firefox
    window.cookieStore.addEventListener("change", (e) => {
        var _a;
        const pieEyeCookie = (_a = e.changed) === null || _a === void 0 ? void 0 : _a.find(c => c.name === PIEEYE_COOKIE_NAME);
        if (pieEyeCookie) {
            console.log('pieeye cookie consent changed (cs)', pieEyeCookie.value);
            onConsentUpdated();
        }
    });
}
else {
    let cachedVal = Cookies.get(PIEEYE_COOKIE_NAME);
    setInterval(() => {
        const newVal = Cookies.get(PIEEYE_COOKIE_NAME);
        if (cachedVal !== newVal) {
            cachedVal = newVal;
            console.log('pieeye cookie consent changed (i)', newVal);
            onConsentUpdated();
        }
    }, 500);
}
function buildCookieObservable(sub) {
    return sub.pipe(filter((val) => val !== undefined), distinctUntilChanged());
}
function buildAllowedOnce(obs) {
    return obs.pipe(filter(val => !!val), take(1));
}
function onConsentUpdated() {
    const consent = getConsent();
    Object.entries(consent).forEach(([key, value]) => {
        const subject = PIEEYE_CONSENT_MAP[key];
        if (subject) {
            subject.next(value === 1);
        }
    });
    updateCart({ attributes: { _pieeye_cookie_consent: window.localStorage.getItem('pieeye-consent') || 'unknown' } });
}
function isEmptyObject(obj) {
    if (obj === null)
        return true;
    if (typeof obj !== "object")
        return true;
    return Object.keys(obj).length === 0;
}
